import { LoaderCircleIcon, LockIcon } from 'lucide-react'

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  SectionHeading,
} from '@/components/ui'
import { SUPPORT_URL } from '@/config'
import type { Project } from '@/modules/projects'
import { isProjectProcessing } from '@/modules/projects/status'
import { REPORT_SECTIONS } from './reportSections'

interface AnalysisViewControlProps {
  project: Project
  children?: React.ReactNode
}

interface ControlCardProps {
  icon?: React.ReactNode
  title: string
  description: string
  actions?: React.ReactNode
}

const ControlCard = ({
  icon,
  title,
  description,
  actions,
}: ControlCardProps) => {
  return (
    <div className="flex items-start justify-center pt-16">
      <Card className="max-w-lg">
        <CardHeader className="items-center text-center pb-2">
          {icon && <div className="mb-2">{icon}</div>}
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <CardDescription className="text-sm leading-relaxed text-ink-subtle">
            {description}
          </CardDescription>
        </CardContent>
        {actions && (
          <CardFooter className="justify-center pb-6">{actions}</CardFooter>
        )}
      </Card>
    </div>
  )
}

function AnalysisNotEnabled() {
  return (
    <div className="max-w-4xl space-y-8">
      <SectionHeading
        title="AI forensic analysis"
        description={
          <span className="block max-w-2xl">
            Automatically generate comprehensive forensic accounting reports in
            minutes. Uncover transaction patterns, undisclosed assets, spending
            habits, and more, all powered by AI.
          </span>
        }
      />

      <div>
        <h3 className="mb-3 text-[13px] font-medium text-ink-subtle">
          What's included
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {REPORT_SECTIONS.map(({ icon: Icon, title, description }) => (
            <Card key={title}>
              <CardContent className="flex items-start gap-3 p-4">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" />
                <div>
                  <p className="text-sm font-medium">{title}</p>
                  <p className="text-xs text-ink-subtle text-pretty">
                    {description}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="flex items-center gap-4 p-4">
          <LockIcon className="h-5 w-5 shrink-0 text-ink-subtle" />
          <div className="flex-1">
            <p className="text-sm font-medium">
              AI Analysis is not enabled on your account
            </p>
            <p className="text-xs text-ink-subtle">
              Contact us to unlock Daystrom for all your projects.
            </p>
          </div>
          <Button asChild size="sm">
            <a href={SUPPORT_URL} target="_blank">
              Contact Us
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default function AnalysisViewControl({
  project,
  children,
}: AnalysisViewControlProps) {
  if (isProjectProcessing(project)) {
    return (
      <ControlCard
        icon={
          <LoaderCircleIcon className="h-6 w-6 text-ink-subtle animate-spin" />
        }
        title="Project still processing"
        description="Daystrom needs your project to finish processing before it can analyze the transactions. This page will be available once processing is complete."
      />
    )
  }

  if (!project.isAiFeaturesEnabled) {
    return <AnalysisNotEnabled />
  }

  if (!project.hasTransactions) {
    return (
      <ControlCard
        title="No transactions found"
        description="No transactions found in this project."
      />
    )
  }
  return children
}
